import React from "react"
import t from "prop-types"
import { Route, Switch, useParams } from "react-router-dom"
import { Paper } from "@material-ui/core"
import { Alert, AlertTitle } from "@material-ui/lab"
import { makeStyles } from "@material-ui/core/styles"
import moment from "moment"
import { SnackbarProvider } from "notistack"
import BookmarkWidget from "./BookmarkWidget"
import DashboardWidget from "./DashboardWidget"
import TemplateWidget from "./TemplateWidget"
import { useQuery } from "../../commons/utils"
import ErrorBoundary from "../../pages/error/ErrorBoundary"

const STANDARD_QUERY_PARAMETERS = [
  "isinCode",
  "assetCode",
  "endDate",
  "visitorProfile",
  "scope",
  "name"
]

const DATE_FORMAT = "YYYY-MM-DD"

const useStyles = makeStyles((/* theme */) => ({
  alert: {
    "& pre": {
      display: "inline",
      margin: 0
    }
  }
}))

// region React error components
function CommonError(props) {
  const classes = useStyles()
  const { error } = props
  if (!error) {
    return null
  }

  const { message } = error
  return (
    <Alert severity="error" className={classes.alert}>
      <AlertTitle>Wrong API usage: {message}</AlertTitle>
      Verify that your route call is respecting following format:{" "}
      <pre>/widget/&#123;type&#125;/&#123;alias&#125;</pre>
      <ul>
        <li>
          <b>type</b>: possible values are [bookmark, dashboard, template]
        </li>
        <li>
          <b>alias</b>: configured widget alias in backend
        </li>
      </ul>
    </Alert>
  )
}

CommonError.propTypes = {
  error: t.object.isRequired
}

function InvalidDateFormat(props) {
  const { date } = props
  return (
    <CommonError
      message={`invalid date format of ${date}. Expect date format YYYY-MM-DD.`}
    />
  )
}

InvalidDateFormat.propTypes = {
  date: t.string.isRequired
}

function MissingType() {
  return <CommonError error={{ message: "missing type" }} />
}

function InvalidType(props) {
  const { type } = props
  return <CommonError error={{ message: `invalid type (${type})` }} />
}

InvalidType.propTypes = {
  type: t.string.isRequired
}

function MissingAlias() {
  return <CommonError error={{ message: "missing alias" }} />
}
// endregion

// region utils & methods
function buildOptions(queryArgs) {
  const isinCode = queryArgs.get("isinCode")
  const assetCode = queryArgs.get("assetCode")
  const startDate = queryArgs.get("startDate")
  const endDate = queryArgs.get("endDate")
  const visitorProfile = queryArgs.get("visitorProfile")
  const scope = queryArgs.get("scope")
  const benchmark = queryArgs.get("benchmark")
  const customer = queryArgs.get("customer")
  const contact = queryArgs.get("contact")

  // FIXME SR: this is a security hole
  // callers can set any kind of assetCode / isin
  // set standalone widget transformer for backend verification
  const options = {}
  if (isinCode) {
    options.isin_list = [isinCode]
  }

  // asset code override ISIN in case of both are given
  if (assetCode) {
    options.asset_list = [assetCode]
  }

  if (visitorProfile) {
    options.visitorProfile = [visitorProfile]
  }

  if (scope) {
    options.scope = scope
  }

  if (benchmark) {
    options.benchmark_list = [benchmark]
  }

  if (customer) {
    options.customer_list = [customer]
  }

  if (contact) {
    options.contact_list = [contact]
  }

  function dateToMoment(dateString) {
    return moment(dateString, DATE_FORMAT, true)
  }

  if (endDate) {
    const endDateMoment = dateToMoment(endDate)
    if (!endDateMoment.isValid()) {
      return <InvalidDateFormat date={endDate} />
    }
    options.end_date_list = [endDateMoment.add(12, "hours").toISOString()]
  }

  if (startDate) {
    const startDateMoment = dateToMoment(startDate)
    if (!startDateMoment.isValid()) {
      return <InvalidDateFormat date={startDate} />
    }

    options.start_date_list = [startDateMoment.add(12, "hours").toISOString()]
  }

  Array.from(queryArgs.entries())
    .filter(([key]) => !STANDARD_QUERY_PARAMETERS.includes(key))
    .forEach(([key, value]) => {
      options[key] = value
    })

  return options
}

function requiredTemplateProps(queryArgs) {
  const name = queryArgs.get("name")
  if (name === undefined || name === null) {
    throw new Error("Missing mandatory argument name")
  }
  if (name.length === 0) {
    throw new Error("Invalid name argument: name can't be empty")
  }

  return { name }
}

function requiredDashboardProps(queryArgs) {
  const showTableTitle = queryArgs.get("showTableTitle") || false
  const showTableTitleBool = showTableTitle === "true"
  const options = {}
  if (!showTableTitleBool) {
    options.TableProps = { renderTitle: () => "" }
  }

  return options
}

/**
 * Defines advanced props used by type
 * @type {{template: (function(*): {name: *})}}
 */
const advancedPropsByType = {
  template: requiredTemplateProps,
  dashboard: requiredDashboardProps
}

/**
 * Defines React widget component by asked type
 * @type {{template: (function(*): *), bookmark: (function(*): (*|null)), dashboard: (function(*): (*|null))}}
 */
const componentByType = {
  bookmark: BookmarkWidget,
  dashboard: DashboardWidget,
  template: TemplateWidget
}
// endregion

/**
 * Main widget solver for third party website integration.
 */
function Widget() {
  const { type, alias } = useParams()
  const queryArgs = useQuery()

  const Component = componentByType[type]
  if (!Component) {
    return <InvalidType type={type} />
  }

  // build advanced props used by widget
  const others =
    advancedPropsByType[type] && advancedPropsByType[type](queryArgs)

  // enhance options according given query arguments
  const bodyOpt = buildOptions(queryArgs)

  // Wrap Widget in Container to get all CSS selector bounded correctly
  // see theme.js > MuiContainer
  // style props has been set to display shadow from Paper component and not cutting it
  return (
    <Component
      widgetId={alias}
      ChartContainerProps={{ component: Paper, elevation: 0 /* GAM style */ }}
      reqOpt={{ bodyOpt }}
      disableLoader
      {...others}
    />
  )
}

/**
 * Add a simple router that validate route calls
 * @returns {JSX.Element}
 * @constructor
 */
export default function StandaloneWidget() {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      preventDuplicate
    >
      <ErrorBoundary FallbackComponent={CommonError}>
        <Switch>
          <Route path="/widget" component={MissingType} exact />
          <Route path="/widget/:type" component={MissingAlias} exact />
          <Route path="/widget/:type/:alias" component={Widget} exact />
        </Switch>
      </ErrorBoundary>
    </SnackbarProvider>
  )
}
